import {
  InviteUser,
  InviteUserError,
  InviteUserSuccess,
  LoadAccountTeamUsers,
  RemoveAccountTeamApiError,
  RemovePendingUserSucceeded,
  RemoveUser,
  RemoveUserConfirmation,
  RemoveUserError,
  RemoveUserSuccess,
  ResetCallStateInviteUser,
  ResetCallStateRemoveUser,
  ResetCallStateUpdateUserRole,
  SetAccountTeamApiError,
  SetAccountTeamUsers,
  UpdateOrganizationRolesError,
  UpdateOrganizationRolesSuccess,
} from './account-team-user.actions';
import { AccountTeamUserApiError } from './account-team-user.errors';
import {
  CallStateInviteUser,
  CallStateLoadUsers,
  CallStateRemoveUser,
  CallStateUpdateUserRole,
} from './account-team-user.enum';
import { OrganizationUser } from './account-team-user.model';
import { createReducer, on } from '@ngrx/store';

export interface AccountTeamUserState {
  organizationUsers: OrganizationUser[];
  callStateLoadUsers: CallStateLoadUsers;
  callStateInviteUser: CallStateInviteUser;
  callStateUpdateUserRole: CallStateUpdateUserRole;
  callStateRemoveUser: CallStateRemoveUser;
  userInEdit: OrganizationUser;
  apiError: AccountTeamUserApiError;
}

export const initialState: AccountTeamUserState = {
  organizationUsers: [],
  callStateLoadUsers: CallStateLoadUsers.INIT,
  callStateInviteUser: CallStateInviteUser.IDLE,
  callStateUpdateUserRole: CallStateUpdateUserRole.IDLE,
  callStateRemoveUser: CallStateRemoveUser.IDLE,
  userInEdit: null,
  apiError: null,
};

export const accountTeamUserReducer = createReducer(
  initialState,
  // Load users
  on(LoadAccountTeamUsers, (state) => ({
    ...state,
    callStateLoadUsers: CallStateLoadUsers.LOADING,
  })),
  on(SetAccountTeamUsers, (state, { users }) => ({
    ...state,
    callStateLoadUsers: CallStateLoadUsers.LOADED,
    organizationUsers: users,
  })),
  on(SetAccountTeamApiError, (state, { error }) => ({
    ...state,
    apiError: error,
    callStateLoadUsers: CallStateLoadUsers.ERROR,
  })),
  on(RemoveAccountTeamApiError, (state) => ({ ...state, apiError: null })),
  // Invite user
  on(InviteUser, (state) => ({ ...state, callStateInviteUser: CallStateInviteUser.IN_PROGRESS })),
  on(InviteUserSuccess, (state, { user }) => ({
    ...state,
    callStateInviteUser: CallStateInviteUser.SUCCESS,
    organizationUsers: [...state.organizationUsers, user],
  })),
  on(InviteUserError, (state, { error }) => ({
    ...state,
    apiError: error,
    callStateInviteUser: CallStateInviteUser.ERROR,
  })),
  on(ResetCallStateInviteUser, (state) => ({
    ...state,
    apiError: null,
    callStateInviteUser: CallStateInviteUser.IDLE,
  })),
  // Remove user
  on(RemoveUser, (state) => ({
    ...state,
    callStateRemoveUser: CallStateRemoveUser.IN_PROGRESS,
  })),
  on(RemoveUserConfirmation, (state, { user }) => ({
    ...state,
    userInEdit: user,
    callStateRemoveUser: CallStateRemoveUser.PENDING,
  })),
  on(RemoveUserSuccess, (state, { userId }) => ({
    ...state,
    apiError: null,
    callStateRemoveUser: CallStateRemoveUser.SUCCESS,
    organizationUsers: state.organizationUsers.filter((user) => user.user_id !== userId),
  })),
  on(RemoveUserError, (state, { error }) => ({
    ...state,
    apiError: error,
    userInEdit: null,
    callStateRemoveUser: CallStateRemoveUser.ERROR,
  })),
  on(ResetCallStateRemoveUser, (state) => ({
    ...state,
    apiError: null,
    userInEdit: null,
    callStateRemoveUser: CallStateRemoveUser.IDLE,
  })),
  // Update role
  on(UpdateOrganizationRolesSuccess, (state, { user }) => ({
    ...state,
    callStateUpdateUserRole: CallStateUpdateUserRole.SUCCESS,
    userInEdit: user,
    organizationUsers: state.organizationUsers.map((existingUser) =>
      existingUser.user_id === user.user_id ? user : existingUser,
    ),
  })),
  on(UpdateOrganizationRolesError, (state, { error }) => ({
    ...state,
    apiError: error,
    userInEdit: null,
    callStateUpdateUserRole: CallStateUpdateUserRole.ERROR,
  })),
  on(ResetCallStateUpdateUserRole, (state) => ({
    ...state,
    apiError: null,
    userInEdit: null,
    callStateUpdateUserRole: CallStateUpdateUserRole.IDLE,
  })),
  on(RemovePendingUserSucceeded, (state, { user }) => ({
    ...state,
    organizationUsers: state.organizationUsers.filter(
      (orgUser) => orgUser.user_id !== user.user_id,
    ),
  })),
);
